import React, { useEffect, useState, useCallback } from 'react';
import { ShoppingCart, X } from 'lucide-react';
import { plannerAPI } from '../../services/api';
import { reminderStore } from '../attendance/reminders';

// On a supplier's order day, the first sign-in of somebody who sends orders is told so: which
// suppliers take orders today and how many items each would carry, and how many items will run out
// before any delivery can arrive. Once a day, and only for suppliers whose order days are set (one
// that takes orders any day would make this a daily nag). Urgent items are told about whatever the
// day, because they cannot wait for one.
//
// It also reports the urgent count upward, for the number on the Medicine Orders menu entry.

const STORE = reminderStore('clinic_orderday');

const OrderDayReminder = ({ authUser, canSend, onOpenOrders, onCount }) => {
  const userId = authUser?.id;
  const [today, setToday] = useState(null);
  const [showing, setShowing] = useState(false);

  const load = useCallback(async () => {
    if (!userId || !canSend) { onCount && onCount(0); return; }
    try {
      const t = await plannerAPI.getToday();
      setToday(t);
      onCount && onCount(Number(t?.orderToday) || 0);
      const due = (t?.suppliersDueToday || []).filter(s => s.hasOrderDays);
      if ((due.length > 0 || t?.orderToday > 0) && STORE.read(userId).state !== 'done') setShowing(true);
    } catch {
      /* the board itself still shows everything */
    }
  }, [userId, canSend, onCount]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const again = () => { if (userId && canSend) plannerAPI.getToday().then(t => onCount && onCount(Number(t?.orderToday) || 0)).catch(() => {}); };
    window.addEventListener('clinic:orders-changed', again);
    return () => window.removeEventListener('clinic:orders-changed', again);
  }, [userId, canSend, onCount]);

  if (!showing || !today) return null;

  const due = (today.suppliersDueToday || []).filter(s => s.hasOrderDays);
  const done = () => { STORE.write(userId, { state: 'done' }); setShowing(false); };
  const open = () => { done(); onOpenOrders && onOpenOrders(); };

  // z-40, one below dialogs: at z-50 this card covered the bottom of an open dialog on a phone,
  // such as the punch window's position line and its buttons (2026-10-21).
  return (
    <div role="alertdialog" aria-label="Orders to place today"
      className="fixed z-40 bottom-4 right-4 left-4 sm:left-auto sm:w-96 bg-white rounded-2xl shadow-2xl border border-navy-200">
      <div className="p-4 flex gap-3">
        <div className="w-10 h-10 rounded-xl bg-navy-50 flex items-center justify-center flex-shrink-0">
          <ShoppingCart className="w-5 h-5 text-navy-700" aria-hidden="true" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="font-semibold text-gray-900">{due.length > 0 ? 'Order day' : 'Items about to run out'}</p>
          <ul className="text-sm text-gray-600 mt-0.5 space-y-0.5">
            {due.map(s => (
              <li key={s.vendorId}><b>{s.vendorName}</b> takes orders today: {s.lines} item{s.lines === 1 ? '' : 's'}{s.urgent > 0 ? `, ${s.urgent} urgent` : ''}.</li>
            ))}
            {today.orderToday > 0 && (
              <li className="text-red-700">{today.orderToday} item{today.orderToday === 1 ? '' : 's'} will run out before any delivery can arrive.</li>
            )}
          </ul>
          <div className="flex flex-wrap gap-2 mt-3">
            <button type="button" onClick={open} className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-sm font-medium">
              Open the order list
            </button>
          </div>
        </div>
        <button type="button" aria-label="Close" onClick={done} className="p-1.5 h-fit hover:bg-gray-100 rounded-lg flex-shrink-0">
          <X className="w-4 h-4 text-gray-500" />
        </button>
      </div>
    </div>
  );
};

export default OrderDayReminder;
